import { zodResolver } from '@hookform/resolvers/zod';
import { usePresentVoiceText, useVoiceCopy } from '@/platform/voice/use-voice';
import {
  ticketCommentFormSchema,
  type TicketComment,
  type TicketCommentFormValues,
} from '@gb-ticket/shared-types';
import { Loader2Icon, LockIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/platform/components/user-avatar';

function commentLooksUnsafe(body: string): boolean {
  return /<\s*script|on\w+\s*=|javascript:/i.test(body);
}

function commentHasMarkup(body: string): boolean {
  return /<\/?[a-z][^>]*>/i.test(body);
}

function CommentBody({ body }: { body: string }) {
  if (!commentHasMarkup(body) || commentLooksUnsafe(body)) {
    return <p className="break-words whitespace-pre-wrap">{body}</p>;
  }
  return (
    <div
      className="break-words [&_a]:underline [&_p]:mb-2 [&_p:last-child]:mb-0"
      dangerouslySetInnerHTML={{ __html: body }}
    />
  );
}

function resizeTextarea(element: HTMLTextAreaElement | null) {
  if (!element) return;
  element.style.height = 'auto';
  element.style.height = `${Math.min(Math.max(element.scrollHeight, 140), 420)}px`;
}

export function TicketComments({
  items,
  requesterId,
  canComment,
  canNoteInternal,
  pending,
  onSubmit,
  draft,
  onDraftConsumed,
}: {
  items: TicketComment[];
  requesterId: string;
  canComment: boolean;
  canNoteInternal: boolean;
  pending: boolean;
  onSubmit: (values: TicketCommentFormValues) => Promise<void>;
  draft?: { body: string; isInternal: boolean } | null;
  onDraftConsumed?: () => void;
}) {
  const copy = useVoiceCopy();
  const present = usePresentVoiceText();
  const form = useForm<TicketCommentFormValues>({
    resolver: zodResolver(ticketCommentFormSchema),
    defaultValues: { body: '', isInternal: false },
  });
  const [isInternal, setIsInternal] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const bodyValue = form.watch('body');
  const { ref: bodyRef, onChange: bodyOnChange, ...bodyField } = form.register('body');

  useEffect(() => {
    if (!draft) {
      return;
    }
    const nextInternal = canNoteInternal && draft.isInternal;
    form.setValue('body', draft.body);
    form.setValue('isInternal', nextInternal);
    setIsInternal(nextInternal);
    onDraftConsumed?.();
    requestAnimationFrame(() => {
      const field = textareaRef.current;
      resizeTextarea(field);
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field?.focus();
    });
  }, [draft, canNoteInternal, form, onDraftConsumed]);

  useEffect(() => {
    resizeTextarea(textareaRef.current);
  }, [bodyValue, isInternal]);

  return (
    <div className="space-y-4">
      {items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {canNoteInternal
            ? 'Noch keine Kommentare.'
            : copy('helpdesk.ticket.comments.empty-portal')}
        </p>
      )}

      <ul className="flex flex-col gap-5 rounded-lg bg-muted px-3 py-5 sm:px-5">
        {items.map((comment) => {
          const fromRequester = comment.author.id === requesterId;
          const fromIt = !fromRequester;
          const internal = comment.isInternal;
          const alignEnd = fromIt || internal;

          return (
            <li
              key={comment.id}
              className={cn('flex w-full', alignEnd ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'flex w-fit max-w-[min(100%,48rem)] min-w-[min(100%,18rem)] flex-col',
                  alignEnd ? 'items-end' : 'items-start',
                )}
              >
                <div
                  className={cn(
                    'w-full rounded-xl border px-4 py-3 text-[0.9375rem] leading-relaxed shadow-xs',
                    internal &&
                      'border-status-warning-border bg-status-warning text-status-warning-foreground',
                    !internal && 'border-border bg-card text-card-foreground',
                  )}
                >
                  <div className="flex items-start gap-4">
                    <span
                      className={cn(
                        'mt-0.5 inline-flex size-9 shrink-0 overflow-hidden rounded-full',
                        !comment.author.profilePhotoUrl &&
                          internal &&
                          'bg-status-warning-foreground text-status-warning',
                        !comment.author.profilePhotoUrl &&
                          !internal &&
                          fromIt &&
                          'bg-primary text-primary-foreground',
                        !comment.author.profilePhotoUrl &&
                          !internal &&
                          fromRequester &&
                          'bg-muted-foreground text-background',
                      )}
                      title={comment.author.displayName}
                      aria-hidden
                    >
                      {internal && !comment.author.profilePhotoUrl ? (
                        <span className="flex size-full items-center justify-center">
                          <LockIcon className="size-4" />
                        </span>
                      ) : (
                        <UserAvatar
                          firstName=""
                          lastName=""
                          displayName={comment.author.displayName}
                          profilePhotoUrl={comment.author.profilePhotoUrl}
                          className={cn(
                            'text-sm',
                            !comment.author.profilePhotoUrl && internal && 'bg-transparent',
                            !comment.author.profilePhotoUrl &&
                              !internal &&
                              fromRequester &&
                              'bg-transparent text-background',
                          )}
                        />
                      )}
                    </span>
                    <div className="min-w-0 flex-1 pl-1">
                      {internal && (
                        <p className="mb-1.5 text-xs font-medium tracking-wide text-status-warning-foreground uppercase">
                          Nur IT
                        </p>
                      )}
                      <CommentBody body={comment.body} />
                    </div>
                  </div>
                </div>
                <p
                  className={cn(
                    'mt-1.5 px-0.5 text-xs text-muted-foreground',
                    alignEnd ? 'text-right' : 'text-left',
                  )}
                >
                  {fromIt && !internal ? 'IT · ' : fromRequester ? 'Kunde · ' : ''}
                  {comment.author.displayName}
                  {' · '}
                  {formatDateTime(comment.createdAt)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>

      {canComment && (
        <form
          id="ticket-comment-form"
          className={cn(
            'space-y-3 rounded-lg border p-4',
            isInternal
              ? 'border-status-warning-border bg-status-warning text-status-warning-foreground'
              : 'border-border bg-card',
          )}
          onSubmit={(event) =>
            void form.handleSubmit(async (values) => {
              await onSubmit({ ...values, isInternal });
              form.reset({ body: '', isInternal: false });
              setIsInternal(false);
              requestAnimationFrame(() => resizeTextarea(textareaRef.current));
            })(event)
          }
        >
          <Label htmlFor="comment">{isInternal ? 'Interne Notiz' : 'Antwort'}</Label>
          <textarea
            id="comment"
            rows={5}
            className="min-h-[9rem] w-full resize-none overflow-hidden rounded-md border border-input bg-background px-4 py-3 text-[0.9375rem] leading-relaxed text-foreground shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            placeholder={
              isInternal ? 'Interne Notiz – nur für die IT sichtbar …' : 'Kommentar schreiben …'
            }
            {...bodyField}
            ref={(element) => {
              bodyRef(element);
              textareaRef.current = element;
              if (element) resizeTextarea(element);
            }}
            onChange={(event) => {
              void bodyOnChange(event);
              resizeTextarea(event.target);
            }}
          />
          {form.formState.errors.body && (
            <p className="text-sm text-destructive">
              {present(form.formState.errors.body.message)}
            </p>
          )}
          {canNoteInternal && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={isInternal}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  setIsInternal(next);
                  form.setValue('isInternal', next);
                }}
              />
              Interne Notiz (nur für IT-Agenten sichtbar)
            </label>
          )}
          <Button type="submit" disabled={pending} variant={isInternal ? 'secondary' : 'default'}>
            {pending && <Loader2Icon className="animate-spin" aria-hidden />}
            {isInternal ? 'Interne Notiz speichern' : 'Kommentar senden'}
          </Button>
        </form>
      )}
    </div>
  );
}
